'use client';

import { use } from 'react';
import { MacaronButtonFillContext, MacaronButtonInnerContext, MacaronNumericQuantityLabelContext } from './scope';
import { GAP } from './tokens';
import type { MacaronRenderContext, RecordAny } from '@macaron-a2ui/core/v0_8';

function childIds(children: unknown): string[] {
  if (!children || typeof children !== 'object') return [];
  const explicit = (children as RecordAny).explicitList;
  return Array.isArray(explicit) ? explicit.filter((id): id is string => typeof id === 'string') : [];
}

const DISTRIBUTED = new Set(['spaceBetween', 'spaceAround', 'spaceEvenly']);
const ADAPTIVE_BENTO_SURFACE_TYPES = new Set(['Card', 'RollPickerCard']);

function mainJustify(distribution?: string): string {
  switch (distribution) {
    case 'center':
      return 'justify-center';
    case 'end':
      return 'justify-end';
    case 'spaceBetween':
      return 'justify-between';
    case 'spaceAround':
      return 'justify-around';
    case 'spaceEvenly':
      return 'justify-evenly';
    default:
      return 'justify-start';
  }
}

function crossAlign(alignment?: string): string {
  switch (alignment) {
    case 'center':
      return 'items-center';
    case 'end':
      return 'items-end';
    case 'stretch':
      return 'items-stretch';
    case 'baseline':
      return 'items-baseline';
    default:
      return 'items-start';
  }
}

export function MacaronRow({ config, ctx }: { config: RecordAny; ctx: MacaronRenderContext }) {
  const ids = childIds(config.children);
  const inButton = use(MacaronButtonInnerContext);
  const gap = inButton ? GAP.rowInButton : GAP.rowDefault;
  const distribution = typeof config.distribution === 'string' ? config.distribution : '';
  const isDistributed = DISTRIBUTED.has(distribution);
  const alignment = typeof config.alignment === 'string' ? config.alignment : '';
  const hasWeightedChildren = ids.some((id) => Boolean(ctx.getComponentWeight?.(id)));
  const isMediaTextRow = ids.length === 2 && ctx.getComponentType?.(ids[0]) === 'Icon' && ctx.getComponentType?.(ids[1]) === 'Column';
  const isAdaptiveFullWidthButtonGroup = !inButton && ids.length > 1 && ids.every((id) => ctx.getComponentType?.(id) === 'Button');
  const isStandaloneFullWidthButton = !inButton && ids.length === 1 && ctx.getComponentType?.(ids[0]) === 'Button';
  const isAdaptiveLiftedTileRow = !inButton && ids.length > 1 && ids.every((id) => ADAPTIVE_BENTO_SURFACE_TYPES.has(ctx.getComponentType?.(id) || '')) && !ids.some((id) => ctx.getComponentWeight?.(id));
  const effectiveDistribution = isAdaptiveFullWidthButtonGroup || isAdaptiveLiftedTileRow ? 'start' : distribution;
  const effectiveAlignment = isAdaptiveLiftedTileRow ? 'stretch' : isMediaTextRow && alignment === 'center' ? 'start' : alignment;
  // Flutter's action rows still keep a fixed inner gutter even when the parent asks for `spaceEvenly`.
  // Without this, web flexbox collapses adjacent CTA buttons until they visually touch, which is what the screenshot showed.
  const shouldApplyGap = ids.length > 1 && (isAdaptiveFullWidthButtonGroup || isAdaptiveLiftedTileRow || !isDistributed || distribution === 'spaceEvenly');

  return (
    <div
      className={`flex min-w-0 ${isDistributed || isAdaptiveFullWidthButtonGroup || isAdaptiveLiftedTileRow || isStandaloneFullWidthButton ? 'w-full' : 'w-auto'} ${mainJustify(effectiveDistribution)} ${crossAlign(effectiveAlignment)}`}
      style={shouldApplyGap ? { gap } : undefined}
    >
      {ids.map((id, index) => {
        const shouldPrefixNumericQuantity = effectiveDistribution === 'spaceBetween' && ids.length === 2 && index === ids.length - 1 && ctx.getComponentType?.(id) === 'Label';
        return (
          <div
            key={id}
            className={`min-w-0 ${isStandaloneFullWidthButton || isAdaptiveLiftedTileRow || (isAdaptiveFullWidthButtonGroup && !hasWeightedChildren) ? 'basis-0 flex-1' : ctx.getComponentWeight?.(id) ? 'basis-0' : 'shrink-0'}`}
            style={ctx.getComponentWeight?.(id) ? { flexGrow: ctx.getComponentWeight?.(id) } : undefined}
          >
            <MacaronButtonFillContext.Provider value={isAdaptiveFullWidthButtonGroup || isStandaloneFullWidthButton}>
              <MacaronNumericQuantityLabelContext.Provider value={shouldPrefixNumericQuantity}>{ctx.renderChild(id)}</MacaronNumericQuantityLabelContext.Provider>
            </MacaronButtonFillContext.Provider>
          </div>
        );
      })}
    </div>
  );
}
